/**
 * Asking a publicly shared view for a range of rows.
 *
 * The same shape as the signed-in fetcher and a different endpoint: addressed
 * by slug, carrying no credential at all, and applying the view's filters
 * entirely on the server. The reader is never told what those filters are --
 * `PublicViewSerializer` leaves them out -- so nothing here could send them even
 * if it wanted to.
 */
import { getApi, unwrap } from '@/api/client'
import type { RowData, ViewGroupBy } from '@/api/types'
import type { GroupByMetadata } from '../grid/grouping'
import type { RowFetcher, RowPage } from '../rows/rowFetcher'

interface PublicRowsResponse {
  count?: number | null
  results?: unknown[]
  group_by_metadata?: GroupByMetadata
}

/**
 * How this endpoint is told what the view is grouped by.
 *
 * The signed-in endpoint reads the view's own group bys and needs telling
 * nothing. This one is told, because the same parameter is what *orders* the
 * rows here -- a public view's ordering is sent by the reader rather than
 * applied from the stored view -- so a fetch that left it out would return the
 * rows in a different order from the one the groups are drawn against, and the
 * runs would be nonsense rather than merely uncounted.
 *
 * The format is the backend's: `-` for a descending term, and the sort type in
 * brackets when it is not the default one.
 */
export function groupByParameter(groupBys: readonly ViewGroupBy[]): string {
  return groupBys
    .map((groupBy) => {
      const direction = groupBy.order === 'DESC' ? '-' : ''
      const type =
        groupBy.type === undefined || groupBy.type === 'default'
          ? ''
          : `[${groupBy.type}]`
      return `${direction}field_${groupBy.field}${type}`
    })
    .join(',')
}

/**
 * A fetcher for one slug.
 *
 * A factory rather than a module-level function, because the slug is not in
 * the query: the window's `viewId` carries it, and the endpoint takes it in the
 * path.
 */
function fetchFromApi(slug: string): RowFetcher {
  return async (query, range, signal): Promise<RowPage> => {
    const groupBy = groupByParameter(query.groupBys)
    const body = await unwrap<PublicRowsResponse>(
      getApi().GET('/api/database/views/grid/{slug}/public/rows/', {
        params: {
          path: { slug },
          query: {
            limit: Math.max(0, range.end - range.start),
            offset: range.start,
            ...(groupBy === '' ? {} : { group_by: groupBy }),
            ...(query.search.term === ''
              ? {}
              : { search: query.search.term, search_mode: query.search.mode }),
          },
        },
        signal,
      }),
    )

    return {
      rows: (body.results ?? []) as RowData[],
      count: body.count ?? undefined,
      ...(body.group_by_metadata === undefined
        ? {}
        : { groupByMetadata: body.group_by_metadata }),
    }
  }
}

/**
 * The factory every public grid uses, swappable for a stub.
 *
 * The same seam as `__setRowFetcherForTests`, for the same reason: every
 * request this makes carries an abort signal, and jsdom's `AbortController` is
 * not the one Node's `Request` will accept -- so a component test that reached
 * the network would fail on the environment rather than on anything real.
 */
let factory: (slug: string) => RowFetcher = fetchFromApi

export function publicRowFetcher(slug: string): RowFetcher {
  return factory(slug)
}

/** Test seam: hand the public grids a fetcher, or `null` to restore the real one. */
export function __setPublicRowFetcherForTests(
  replacement: ((slug: string) => RowFetcher) | null,
): void {
  factory = replacement ?? fetchFromApi
}
